import { HttpParams } from '@angular/common/http';
import {
  Component,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Optional
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationItem } from '@app/core/models/notification-item';
import { ExtranetAuthenticationService } from '@src/app/core/services/extranet-authentication.service';
import { ExtranetMenuService } from '@src/app/core/services/extranet-menu.service';
import {
  GlobalJobSchedulerService,
  RepeatableJob,
} from '@src/app/core/services/global-job-scheduler.service';
import { HeaderService } from '@src/app/core/services/header.service';
import { PasswordService } from '@src/app/core/services/password.service';
import { SelectItem } from 'primeng/api';
import { isNullOrEmpty } from 'projects/sigv-library/src/util/src/public-api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AppConfig, BaseComponent,
  StorageService,
  UserProfileService
} from 'sigv-library';
import { UserProfile } from 'sigv-library/model';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_KEY,
} from 'src/app/core/constants/global-constants.constant';
import { NotificationService } from 'src/app/core/services/notification-data.service';
import { SystemEnvService } from 'src/app/core/services/system-env.service';

@Component({
  selector: 'sigv-extranet-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  private readonly destroy$ = new Subject();
  @Input() isAtLogin = false;
  locale = DEFAULT_LANGUAGE;
  isVisible = true;
  languageOptions: SelectItem[] = [];
  userProfile: UserProfile | null = null;
  deptName?: string;
  notifications: NotificationItem[] = [];
  unReadNotificationCount = 0;
  private updateNotificationCountJob: RepeatableJob = {
    jobName: 'Refresh Notification Job',
    jobAction: () => {
      this.updateNotificationCount();
    },
  };

  constructor(
    injector: Injector,
    private authenticationService: ExtranetAuthenticationService,
    private userProfileService: UserProfileService,
    private menuService: ExtranetMenuService,
    private notificationService: NotificationService,
    private storageService: StorageService,
    private systemEnvService: SystemEnvService,
    private router: Router,
    @Optional() private globalJobScheculer: GlobalJobSchedulerService,
    private route: ActivatedRoute,
    private passwordService: PasswordService,
    private headerService: HeaderService
  ) {
    super(injector);
    const languageList: any[] = this.envConfig.languageList ?? [];
    this.languageOptions = (
      languageList.length === 0
        ? [{ name: 'English', value: DEFAULT_LANGUAGE }]
        : languageList
    ).map((x) => ({ label: x.name, value: x.value }));

    this.notificationService.updateReadCount$.subscribe(value => {
      this.updateNotificationCount();
    });
  }

  get envConfig(): any {
    return AppConfig.envConfig;
  }

  ngOnInit(): void {
    this.headerService
      .getHeaderObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (isOpen) => {
          this.isVisible = isOpen;
        },
      });

    this.userProfile = this.userProfileService.currentUserValue;
    this.userProfileService.currentUserSubject.subscribe({
      next: (profile) => {
        this.userProfile = profile;
        this.deptName = profile?.userJobTitles?.find(
          (x) => x.mainDuty
        )?.department?.departmentName;
      },
    });

    const urlLocale = this.route.snapshot.queryParams.lang;
    const localeVal =
      urlLocale ?? this.storageService.local.getItem(LANGUAGE_KEY);
    this.locale =
      this.languageOptions.find((x) => x.value === localeVal)?.value ??
      DEFAULT_LANGUAGE;
    this.storageService.local.setItem(LANGUAGE_KEY, this.locale);
    this.translateService.use(this.locale);
    if (!this.isAtLogin) {
      this.globalJobScheculer.addJob(this.updateNotificationCountJob);
      this.updateNotificationCount();
    }
  }

  ngOnDestroy(): void {
    if (this.globalJobScheculer) {
      this.globalJobScheculer.removeJob(this.updateNotificationCountJob);
    }
  }

  toggleSideMenu(): void {
    this.menuService.toggleSideMenu();
  }

  onLogoClick(): void {
    this.router.navigate(['/']);
  }

  onLanguageChange(lang: string): void {
    this.locale = lang;
    this.translateService.use(this.locale);
    this.storageService.local.setItem(LANGUAGE_KEY, lang);
  }

  onLogoutBtnClick(): void {
    this.router.navigate(['/logout']);
  }

  onChangePasswordLinkClick(): void {
    this.router.navigate(['/changePassword']);
  }

  async onNotificationLinkClick(notification: NotificationItem): Promise<void> {
    const dataStr = JSON.stringify(notification);

    if (isNullOrEmpty(notification.systemId) || isNullOrEmpty(notification.url)) {
      return;
    }

    // 撈出外網systemId
    var system = await this.notificationService.getWebsubmissionSystem();

    // 如果撈出來是進件 就在自己頁面導轉
    if (
      notification.systemId === system.systemId
    ) {
      const encodeData = encodeURIComponent(dataStr);
      this.router.navigate(['notification', 'redirection'], {
        queryParams: { data: encodeData },
      });
    } else {
      this.notificationService.updateToRead([notification.id!]).toPromise();
      
      var systemHostUrl = '';
      this.notificationService.getSystemUrl(notification.systemId!).subscribe((system) => {
        systemHostUrl = (system.systemHostUrl ?? '').replace(/\/$/, ''); // 移除結尾的 "/"
      });


      var notificationUrl = notification.url;

      this.authenticationService
        .crossDomainLogin(this.authenticationService.idToken)
        .subscribe({
          next: (sessionId) => {
            const timestamp = new Date().getTime() / 1000;
            const secretStr = JSON.stringify({
              key: sessionId,
              timestamp,
            });
            const encryptedSecret =
              this.passwordService.getEncryptedData(secretStr);
            const params = new HttpParams()
              .append(
                'email',
                encodeURIComponent(
                  this.userProfileService.currentUserValue!.email!
                )
              )
              .append('secret', encodeURIComponent(encryptedSecret))
              .append(
                'redirectTo',
                encodeURIComponent(notificationUrl ?? '')
              )
              .append('data', encodeURIComponent(dataStr));
            const url = `${
              systemHostUrl
              }/redirection?${params.toString()}`;
            window.location.href = url;
          },
        });
    }
  }

  onNotificationMoreBtnClick(): void {
    this.router.navigate(['notification', 'list']);
  }

  updateNotification(): void {
    this.notifications = [];
    this.notificationService.getNotifications(5).subscribe({
      next: (results) => {
        this.notifications = results;
      },
    });
  }

  private updateNotificationCount(): void {
    this.notificationService.getUnReadCount().subscribe({
      next: (result) => {
        this.unReadNotificationCount = result;
      },
    });
  }
}
